import { 
  collection, 
  doc, 
  getDoc,
  getDocs,
  addDoc, 
  updateDoc,
  deleteDoc,
  query, 
  where,
  onSnapshot,
  serverTimestamp
} from 'firebase/firestore';
import { db } from './config';
import { updateBill } from './billingService';

/**
 * Create or update a creditor when a credit bill is created
 */
export const createOrUpdateCreditor = async (customerId, billData) => {
  try {
    // Check if creditor already exists for this customer
    const creditorsRef = collection(db, 'creditors');
    const q = query(creditorsRef, where('customerId', '==', customerId));
    const snapshot = await getDocs(q);

    const billAmount = parseFloat(billData.total || 0);
    const paymentType = billData.paymentType || 'Credit';
    const category = paymentType === 'Koko' ? 'Koko' : (paymentType === 'MintPay' ? 'MintPay' : (paymentType === 'COD' ? 'COD' : 'Credit'));

    if (!snapshot.empty) {
      // Update existing creditor
      const creditorDoc = snapshot.docs[0];
      const creditorData = creditorDoc.data();
      
      const newTotalCredited = (creditorData.totalCredited || 0) + billAmount;
      const newBalance = newTotalCredited - (creditorData.totalPaid || 0);
      
      await updateDoc(doc(db, 'creditors', creditorDoc.id), {
        totalCredited: newTotalCredited,
        balance: newBalance,
        bills: [...(creditorData.bills || []), billData.id],
        status: newBalance > 0 ? 'Active' : 'Settled',
        category: category,
        dueDate: billData.dueDate || null,
        updatedAt: serverTimestamp()
      });

      return {
        success: true,
        creditorId: creditorDoc.id,
        message: 'Creditor updated successfully'
      };
    } else {
      // Create new creditor
      const newCreditor = {
        customerId: customerId,
        customerName: billData.customerName || billData.customer,
        customerPhone: billData.customerPhone || '',
        customerEmail: billData.customerEmail || '',
        totalCredited: billAmount,
        totalPaid: 0,
        balance: billAmount,
        status: 'Active',
        category: category,
        bills: [billData.id],
        dueDate: billData.dueDate || null,
        invoiceDate: billData.invoiceDate || null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(collection(db, 'creditors'), newCreditor);

      return {
        success: true,
        creditorId: docRef.id,
        message: 'Creditor created successfully'
      };
    }
  } catch (error) {
    console.error('Error creating/updating creditor:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Subscribe to real-time creditors updates
 */
export const subscribeToCreditors = (callback) => {
  try {
    const creditorsRef = collection(db, 'creditors');
    
    return onSnapshot(creditorsRef, (snapshot) => {
      const creditors = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate?.() || new Date()
      }));
      callback(creditors);
    }, (error) => {
      console.error('Error subscribing to creditors:', error);
      callback([]);
    });
  } catch (error) {
    console.error('Error setting up creditors subscription:', error);
    return () => {};
  }
};

/**
 * Get a single creditor by ID
 */
export const getCreditorById = async (creditorId) => {
  try {
    const docRef = doc(db, 'creditors', creditorId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return {
        success: true,
        data: {
          id: docSnap.id,
          ...docSnap.data(),
          createdAt: docSnap.data().createdAt?.toDate?.() || new Date(),
          updatedAt: docSnap.data().updatedAt?.toDate?.() || new Date()
        }
      };
    } else {
      return {
        success: false,
        error: 'Creditor not found'
      };
    }
  } catch (error) {
    console.error('Error getting creditor:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Add a payment to a creditor
 * Handles all payment scenarios properly
 */
export const addPayment = async (creditorId, paymentData) => {
  try {
    const paymentAmount = parseFloat(paymentData.amount || 0);
    const selectedBillIds = paymentData.selectedBills || [];

    // Get creditor data first
    const creditorRef = doc(db, 'creditors', creditorId);
    const creditorSnap = await getDoc(creditorRef);
    
    if (!creditorSnap.exists()) {
      return { success: false, error: 'Creditor not found' };
    }

    const creditorData = creditorSnap.data();
    const currentBalance = creditorData.balance || 0;
    const activeBillIds = creditorData.bills || [];
    let settledBills = creditorData.settledBills || [];
    
    // VALIDATION: Payment cannot exceed balance
    if (paymentAmount > currentBalance) {
      return {
        success: false,
        error: `Payment amount (LKR ${paymentAmount.toFixed(2)}) cannot exceed outstanding balance (LKR ${currentBalance.toFixed(2)})`
      };
    }

    // Get invoice numbers for payment history
    let invoiceNumbers = [];
    let billsToSettle = [];
    
    // SCENARIO 1: Specific invoices selected
    if (selectedBillIds.length > 0) {
      // Fetch selected bills to get their details and total
      const billsQuery = query(collection(db, 'bills'), where('__name__', 'in', selectedBillIds));
      const billsSnapshot = await getDocs(billsQuery);
      
      billsToSettle = billsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      invoiceNumbers = billsToSettle.map(b => b.invoiceNumber || b.id);
      
      // Mark selected bills as paid and move to settled
      for (const bill of billsToSettle) {
        // Update bill status to Paid
        await updateBill(bill.id, {
          status: 'Paid',
          updatedAt: serverTimestamp()
        });
        
        // Add to settled bills (avoid duplicates)
        const alreadySettled = settledBills.some(sb => sb.billId === bill.id);
        if (!alreadySettled) {
          settledBills.push({
            billId: bill.id,
            invoiceNumber: bill.invoiceNumber || bill.id,
            amount: bill.total || 0,
            date: bill.date,
            paymentType: bill.paymentType,
            settledAt: new Date().toISOString()
          });
        }
      }
      
      // Remove settled bills from active bills
      const updatedActiveBills = activeBillIds.filter(id => !selectedBillIds.includes(id));
      
      // Calculate new totals
      const newTotalPaid = (creditorData.totalPaid || 0) + paymentAmount;
      const newBalance = (creditorData.totalCredited || 0) - newTotalPaid;
      const newStatus = updatedActiveBills.length === 0 || newBalance <= 0 ? 'Settled' : 'Active';
      
      // Add payment record
      await addDoc(collection(db, 'creditors', creditorId, 'payments'), {
        amount: paymentAmount,
        date: paymentData.date || new Date().toISOString().split('T')[0],
        notes: paymentData.notes || '',
        selectedBills: selectedBillIds,
        invoiceNumbers: invoiceNumbers,
        createdAt: serverTimestamp()
      });
      
      // Update creditor
      await updateDoc(creditorRef, {
        bills: updatedActiveBills,
        settledBills: settledBills,
        totalPaid: newTotalPaid,
        balance: Math.max(0, newBalance),
        status: newStatus,
        settledAt: newStatus === 'Settled' ? serverTimestamp() : null,
        updatedAt: serverTimestamp()
      });
      
      return {
        success: true,
        message: `Payment added. ${selectedBillIds.length} invoice(s) marked as paid.`
      };
    }
    
    // SCENARIO 2: No invoices selected (General Payment)
    else {
      const newTotalPaid = (creditorData.totalPaid || 0) + paymentAmount;
      const newBalance = (creditorData.totalCredited || 0) - newTotalPaid;
      
      // If payment settles full balance, mark ALL bills as paid
      if (newBalance <= 0.01) { // Allow 1 cent tolerance
        // Fetch all active bills
        if (activeBillIds.length > 0) {
          const billsQuery = query(collection(db, 'bills'), where('__name__', 'in', activeBillIds));
          const billsSnapshot = await getDocs(billsQuery);
          
          billsToSettle = billsSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          
          invoiceNumbers = billsToSettle.map(b => b.invoiceNumber || b.id);
          
          // Mark all bills as paid
          for (const bill of billsToSettle) {
            await updateBill(bill.id, {
              status: 'Paid',
              updatedAt: serverTimestamp()
            });
            
            // Add to settled bills (avoid duplicates)
            const alreadySettled = settledBills.some(sb => sb.billId === bill.id);
            if (!alreadySettled) {
              settledBills.push({
                billId: bill.id,
                invoiceNumber: bill.invoiceNumber || bill.id,
                amount: bill.total || 0,
                date: bill.date,
                paymentType: bill.paymentType,
                settledAt: new Date().toISOString()
              });
            }
          }
        }
        
        // Add payment record
        await addDoc(collection(db, 'creditors', creditorId, 'payments'), {
          amount: paymentAmount,
          date: paymentData.date || new Date().toISOString().split('T')[0],
          notes: paymentData.notes || '',
          selectedBills: [],
          invoiceNumbers: invoiceNumbers,
          createdAt: serverTimestamp()
        });
        
        // Update creditor as settled
        await updateDoc(creditorRef, {
          bills: [],
          settledBills: settledBills,
          totalPaid: newTotalPaid,
          balance: 0,
          status: 'Settled',
          settledAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
        
        return {
          success: true,
          message: 'Payment added. All invoices settled!'
        };
      }
      
      // Partial payment - just reduce balance, don't mark any bills as paid
      else {
        // Add payment record
        await addDoc(collection(db, 'creditors', creditorId, 'payments'), {
          amount: paymentAmount,
          date: paymentData.date || new Date().toISOString().split('T')[0],
          notes: paymentData.notes || '',
          selectedBills: [],
          invoiceNumbers: [],
          createdAt: serverTimestamp()
        });
        
        // Update creditor
        await updateDoc(creditorRef, {
          totalPaid: newTotalPaid,
          balance: newBalance,
          updatedAt: serverTimestamp()
        });
        
        return {
          success: true,
          message: 'Partial payment added successfully.'
        };
      }
    }
  } catch (error) {
    console.error('Error adding payment:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Subscribe to payments for a specific creditor
 */
export const subscribeToPayments = (creditorId, callback) => {
  try {
    const paymentsRef = collection(db, 'creditors', creditorId, 'payments');
    
    return onSnapshot(paymentsRef, (snapshot) => {
      const payments = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || new Date()
      }));
      callback(payments);
    }, (error) => {
      console.error('Error subscribing to payments:', error);
      callback([]);
    });
  } catch (error) {
    console.error('Error setting up payments subscription:', error);
    return () => {};
  }
};

/**
 * Update creditor status manually
 */
export const updateCreditorStatus = async (creditorId, status) => {
  try {
    const creditorRef = doc(db, 'creditors', creditorId);
    await updateDoc(creditorRef, {
      status: status,
      updatedAt: serverTimestamp()
    });

    return {
      success: true,
      message: 'Status updated successfully'
    };
  } catch (error) {
    console.error('Error updating creditor status:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Get all creditors (non-realtime)
 */
export const getAllCreditors = async () => {
  try {
    const creditorsRef = collection(db, 'creditors');
    const snapshot = await getDocs(creditorsRef);
    
    const creditors = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate?.() || new Date(),
      updatedAt: doc.data().updatedAt?.toDate?.() || new Date()
    }));

    return {
      success: true,
      data: creditors
    };
  } catch (error) {
    console.error('Error getting creditors:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Remove a bill from creditor when bill is deleted
 * Deletes creditor if no bills remain (regardless of payment history)
 */
export const removeBillFromCreditor = async (creditorId, billId, billAmount) => {
  try {
    const creditorRef = doc(db, 'creditors', creditorId);
    const creditorSnap = await getDoc(creditorRef);
    
    if (!creditorSnap.exists()) {
      return { success: false, error: 'Creditor not found' };
    }
    
    const creditorData = creditorSnap.data();
    
    // Remove bill from bills array
    const updatedBills = (creditorData.bills || []).filter(id => id !== billId);
    
    // Also remove from settled bills if it exists there
    let updatedSettledBills = (creditorData.settledBills || []).filter(sb => sb.billId !== billId);
    
    // If no bills left (active or settled), DELETE the creditor entirely
    // Payment history without bills doesn't make sense
    if (updatedBills.length === 0 && updatedSettledBills.length === 0) {
      await deleteDoc(creditorRef);
      return {
        success: true,
        message: 'Creditor removed (no remaining bills)'
      };
    }
    
    // If bills still remain, update the creditor
    const newTotalCredited = Math.max(0, (creditorData.totalCredited || 0) - parseFloat(billAmount || 0));
    const newBalance = newTotalCredited - (creditorData.totalPaid || 0);
    const newStatus = updatedBills.length === 0 || newBalance <= 0 ? 'Settled' : 'Active';
    
    await updateDoc(creditorRef, {
      bills: updatedBills,
      settledBills: updatedSettledBills,
      totalCredited: newTotalCredited,
      balance: Math.max(0, newBalance),
      status: newStatus,
      settledAt: newStatus === 'Settled' ? serverTimestamp() : creditorData.settledAt || null,
      updatedAt: serverTimestamp()
    });
    
    return {
      success: true,
      message: newStatus === 'Settled' 
        ? 'Bill removed. Creditor marked as settled.'
        : 'Bill removed from creditor successfully'
    };
  } catch (error) {
    console.error('Error removing bill from creditor:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Handle bill return/refund - reduces totalCredited, NOT a payment
 * Used when a bill is returned (full or partial)
 */
export const recordBillReturn = async (creditorId, billId, returnAmount, returnType = 'full') => {
  try {
    const creditorRef = doc(db, 'creditors', creditorId);
    const creditorSnap = await getDoc(creditorRef);
    
    if (!creditorSnap.exists()) {
      return { success: false, error: 'Creditor not found' };
    }
    
    const creditorData = creditorSnap.data();
    
    // Reduce totalCredited (the bill amount is being reversed)
    const newTotalCredited = Math.max(0, (creditorData.totalCredited || 0) - returnAmount);
    const newBalance = newTotalCredited - (creditorData.totalPaid || 0);
    
    // If full return, remove bill from active bills
    let updatedBills = creditorData.bills || [];
    if (returnType === 'full') {
      updatedBills = updatedBills.filter(id => id !== billId);
    }
    
    // Determine new status
    const newStatus = updatedBills.length === 0 || newBalance <= 0 ? 'Settled' : 'Active';
    
    // Record the return in a returns subcollection (for history)
    await addDoc(collection(db, 'creditors', creditorId, 'returns'), {
      billId: billId,
      amount: returnAmount,
      returnType: returnType, // 'full' or 'partial'
      date: new Date().toISOString().split('T')[0],
      createdAt: serverTimestamp()
    });
    
    // Update creditor
    await updateDoc(creditorRef, {
      bills: updatedBills,
      totalCredited: newTotalCredited,
      balance: Math.max(0, newBalance),
      status: newStatus,
      settledAt: newStatus === 'Settled' ? serverTimestamp() : creditorData.settledAt || null,
      updatedAt: serverTimestamp()
    });
    
    return {
      success: true,
      message: `Return recorded. ${returnType === 'full' ? 'Bill removed from creditor.' : 'Creditor balance adjusted.'}`
    };
  } catch (error) {
    console.error('Error recording bill return:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Clean up orphaned creditors (no bills and no payment history)
 * Useful for maintenance or after bulk bill deletions
 */
export const cleanupOrphanedCreditors = async () => {
  try {
    const creditorsRef = collection(db, 'creditors');
    const snapshot = await getDocs(creditorsRef);
    
    const deletePromises = [];
    
    for (const creditorDoc of snapshot.docs) {
      const creditorData = creditorDoc.data();
      const bills = creditorData.bills || [];
      
      // If no bills, check for payments
      if (bills.length === 0) {
        const paymentsRef = collection(db, 'creditors', creditorDoc.id, 'payments');
        const paymentsSnapshot = await getDocs(paymentsRef);
        
        // If no payments either, delete this orphaned creditor
        if (paymentsSnapshot.empty) {
          deletePromises.push(
            deleteDoc(doc(db, 'creditors', creditorDoc.id))
              .then(() => {
                console.log(`Deleted orphaned creditor: ${creditorDoc.id}`);
                return 1; // Return 1 to count deletions
              })
          );
        }
      }
    }
    
    const results = await Promise.all(deletePromises);
    const deletedCount = results.reduce((sum, val) => sum + (val || 0), 0);
    
    return {
      success: true,
      deletedCount,
      message: `Cleaned up ${deletedCount} orphaned creditor(s)`
    };
  } catch (error) {
    console.error('Error cleaning up orphaned creditors:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Clean up creditor's bills array to remove non-existent bill IDs
 * and recalculate totalCredited based on actual bills
 */
export const cleanupCreditorBills = async (creditorId, validBillIds) => {
  try {
    const creditorRef = doc(db, 'creditors', creditorId);
    const creditorSnap = await getDoc(creditorRef);
    
    if (!creditorSnap.exists()) {
      return { success: false, error: 'Creditor not found' };
    }
    
    const creditorData = creditorSnap.data();
    
    // If no valid bills left, check for payments
    if (validBillIds.length === 0) {
      const paymentsRef = collection(db, 'creditors', creditorId, 'payments');
      const paymentsSnapshot = await getDocs(paymentsRef);
      
      // If no payments, delete the creditor
      if (paymentsSnapshot.empty) {
        await deleteDoc(creditorRef);
        return {
          success: true,
          message: 'Creditor removed (no valid bills or payments)'
        };
      }
      
      // If payments exist, mark as settled
      await updateDoc(creditorRef, {
        bills: [],
        balance: 0,
        status: 'Settled',
        settledAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      return {
        success: true,
        message: 'Creditor cleaned up and marked as settled'
      };
    }
    
    // Recalculate totalCredited from actual bills
    const billsRef = collection(db, 'bills');
    const billsQuery = query(billsRef, where('__name__', 'in', validBillIds));
    const billsSnapshot = await getDocs(billsQuery);
    
    let newTotalCredited = 0;
    billsSnapshot.forEach(billDoc => {
      newTotalCredited += billDoc.data().total || 0;
    });
    
    const newBalance = newTotalCredited - (creditorData.totalPaid || 0);
    const newStatus = newBalance <= 0 ? 'Settled' : 'Active';
    
    // Update creditor with cleaned data
    await updateDoc(creditorRef, {
      bills: validBillIds,
      totalCredited: newTotalCredited,
      balance: Math.max(0, newBalance),
      status: newStatus,
      settledAt: newStatus === 'Settled' ? serverTimestamp() : null,
      updatedAt: serverTimestamp()
    });
    
    return {
      success: true,
      message: 'Creditor bills cleaned up successfully'
    };
  } catch (error) {
    console.error('Error cleaning up creditor bills:', error);
    return {
      success: false,
      error: error.message
    };
  }
};
